import React, { useEffect, useState } from 'react'
import { Button } from '../../components/atoms/button/button'
import BusinessAccount from '../../components/organisms/business-account/business-account'
import CustomModal from '../../components/molecules/modal/modal'
import { InputField } from '../../components/molecules/input/input-field'
import { t } from '../../utils/i18n'
import axios from '../../utils/axios'
import { useAuth } from '../../customHooks/useAuth'
import { showToast } from '../../components/atoms/toaster/toaster'
import { InfoTooltip } from '../../components/atoms/info-tooltip/info-tooltip'
import CustomDropdown, { DropdownOption } from '../../components/molecules/dropdown/dropdown'
import { BusinessManagerStatus } from '../../types'
import { useSearchParams } from 'react-router-dom'

type FormData = {
  businessId: string
  businessName: string
  token: string
}

const bmStatusOptions: DropdownOption[] = [
  {
    id: 'all',
    name: BusinessManagerStatus.all,
  },
  {
    id: 'unmapped',
    name: BusinessManagerStatus.unmapped,
  },
  {
    id: 'mapped',
    name: BusinessManagerStatus.mapped,
  },
  {
    id: 'inprogress',
    name: BusinessManagerStatus.in_progress,
  },
]

export const BusinessAccounts = () => {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const bmParamStatus = searchParams.get('status')

  const [isModalOpen, isSetModalOpen] = useState(false)
  const [isLoading, isSetLoading] = useState(false)
  const [isBMOnboarded, isSetBMOnboarded] = useState(false)
  const [formData, setFormData] = useState<FormData>({
    businessId: '',
    businessName: '',
    token: '',
  })
  const [selectedBMStatus, setSelectedBMStatus] = useState<DropdownOption>(() => {
    if (bmParamStatus) {
      return bmStatusOptions.find((status) => status.id === bmParamStatus) as DropdownOption
    } else {
      return bmStatusOptions[0]
    }
  })

  const handleInputChange = (label: string) => (value?: string) => {
    const keyMap: Record<string, keyof typeof formData> = {
      'Business ID': 'businessId',
      'Business Name': 'businessName',
      Token: 'token',
    }

    const fieldKey = keyMap[label]

    if (fieldKey) {
      setFormData((prev) => ({
        ...prev,
        [fieldKey]: value || '',
      }))
    }
  }

  const handleBMAddAcc = async () => {
    isSetLoading(true)
    try {
      const config = {
        url: process.env.REACT_APP_ADD_BUSINESS_ACCOUNTS,
        method: 'POST',
        data: {
          id: formData?.businessId,
          name: formData?.businessName,
          token: formData?.token,
        },
        headers: {
          'x-user': user?.email,
          'x-access-token': user?.tokens?.accessToken?.token,
          'x-source': user?.isSSO ? 'sso' : null,
        },
      }
      const res = await axios(config)
      if (res?.data?.data?.success) {
        showToast({
          message: t('add.business.account.success.message'),
        })
        setFormData({ businessId: '', businessName: '', token: '' })
        isSetBMOnboarded(true)
        isSetLoading(false)
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        if (error?.response?.data?.data?.code) {
          showToast({
            message:
              error?.response?.data?.data?.code &&
              Object.prototype.hasOwnProperty.call(
                t,
                `add.business.account.failed.${error?.response?.data?.data?.code}`
              )
                ? t(`add.business.account.failed.${error?.response?.data?.data?.code}`)
                : t('add.business.account.generic.error'),
            isError: true,
          })
        } else {
          showToast({
            message: t('add.business.account.generic.error'),
            isError: true,
          })
        }
        setFormData({ businessId: '', businessName: '', token: '' })
        isSetLoading(false)
      }
    }
    isSetModalOpen(false)
  }

  const handleStatusChange = (option: DropdownOption) => {
    const status = option.id.toUpperCase()

    setSelectedBMStatus(option)
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev)
        sp.set('status', status)
        return sp
      },
      { replace: false }
    )
  }

  useEffect(() => {
    if (bmParamStatus) {
      const dropdownOption = bmStatusOptions.find(
        (status) => status?.id === bmParamStatus?.toLowerCase()
      )

      setSelectedBMStatus(dropdownOption ? dropdownOption : bmStatusOptions[0])
    }
  }, [])

  const handleModalClick = () => {
    isSetModalOpen(!isModalOpen)
    isSetBMOnboarded(false)
  }

  return (
    <div className="dashboard">
      <div className="link-account-container dashboard-inner-container">
        <div className="top-section">
          <div className="dropdown-wrapper">
            <div className="title-info">
              <p className="label-input">{t('business.manager.status')}</p>
              <InfoTooltip
                styleClass="input-info"
                tooltipContent={t('manage.advertiser.dropdown.tooltip')}
                placement={'right'}
              />
            </div>
            <div className="dropdown">
              <CustomDropdown
                id="advertiser-selector"
                className="label-input"
                label=""
                placeholder={t('advertiser.dropdown.placeholder')}
                options={bmStatusOptions}
                onSelect={handleStatusChange}
                defaultSelectedId={selectedBMStatus?.id}
              />
            </div>
          </div>
          <Button label={t('add.business.account')} handleClick={handleModalClick} />
          <CustomModal
            isOpen={isModalOpen}
            title={t('add.business.account')}
            continueTitle={t('add.business.account.cta')}
            abortTitle={t('add.business.account.abort')}
            isSetModalOpen={isSetModalOpen}
            isFormIncomplete={!formData.businessId || !formData.businessName || !formData.token}
            handleSubmit={handleBMAddAcc}
            isLoading={isLoading}
            body={
              <section>
                <InputField
                  required
                  fieldType="text"
                  label="Business ID"
                  value={formData.businessId}
                  onChange={handleInputChange('Business ID')}
                />

                <InputField
                  required
                  fieldType="text"
                  label="Business Name"
                  value={formData.businessName}
                  onChange={handleInputChange('Business Name')}
                />

                <InputField
                  required
                  fieldType="text"
                  label="Token"
                  value={formData.token}
                  onChange={handleInputChange('Token')}
                />
              </section>
            }
          />
        </div>
        <div className="bottom-section">
          <BusinessAccount
            isSetModalOpen={isSetModalOpen}
            isBMOnboarded={isBMOnboarded}
            bmStatus={selectedBMStatus}
          />
        </div>
      </div>
    </div>
  )
}

export default BusinessAccounts
